import {createContext, useContext, useEffect, useRef, useState} from 'react';
import IProvider from '@models/IProvider';
import {useRouter} from 'next/router';
import moment from 'moment';

type ISearchProvider = {
  registrationQuery: any;
  fromDate: any;
  toDate: any;
  globals: any;
  registrationQueryUpdate: (obj: any) => void;
  fromDateUpdate: (obj: any) => void;
  toDateUpdate: (obj: any) => void;
  resetRegistrationQuery: () => void;
};

const SearchContext = createContext({
  date: moment(),
  globals: null,
  registrationQuery: null,
  fromDate: null,
  toDate: null,
  registrationQueryUpdate: null,
  fromDateUpdate: null,
  toDateUpdate: null,
  resetRegistrationQuery: null,
} as ISearchProvider);

const queryDefaults = {
  timeblockId: null,
  date: moment().format('YYYY-MM-DD'),
  forVal: null,
  forName: null,
  forType: null,
  forId: null,
  forSubject: null
};

// https://localhost:3000/?ageUdis=c36c30d041b149ed8eceeadd28cd2331&durationUdis=a9b6e2709f5c43139cf1c1a623e4dd17&stuffUdis=afdf8f2cca964e84ad944e3d8d7e8362&peopleUdis=50a28b9616254b3ca29aa5daaf26747a
// https://localhost:3000/?timeblockId=312&date=2021-11-10&registrationIndex=4
// https://localhost:3000/?forName=HTX%20Hold%2023&forVal=305&forType=COURSE
export function SearchProvider({ children }: IProvider) {
  const [globals, setGlobals] = useState(null);
  const [registrationQuery, setRegistrationQuery] = useState({ ...queryDefaults });
  const [fromDate, setFromDate] = useState({ ...queryDefaults });
  const [toDate, setToDate] = useState({ ...queryDefaults });
  const router = useRouter();

  useEffect(() => {
    handleUrlQuery();
  }, [router?.pathname]);

  const prevPathname = useRef(router?.pathname);
  useEffect(() => {
    const prev = prevPathname.current;
    const current = router?.pathname;
    prevPathname.current = current;
    const isRegistrationPath = (p: string) => p === '/' || p === '/registration';
    if (prev !== current && isRegistrationPath(prev) && !isRegistrationPath(current)) {
      setRegistrationQuery((old) => ({ ...old, forVal: null, forName: null, forType: null, forId: null, forSubject: null }));
    }
  }, [router?.pathname]);

  useEffect(() => {
    if (router.route === '/') {
      updateHistory();
    }
  }, [registrationQuery]);

  function resetRegistrationQuery() {
    //  reset every value in registrationQuery to null
    setRegistrationQuery({ ...queryDefaults });
  }

  function registrationQueryUpdate(obj) {
    // loop object for key name and value and apply to registrationQuery
    if (obj !== null) {
      const newQuery = { ...registrationQuery, ...obj };
      setRegistrationQuery(newQuery);
    }
  }
  function fromDateUpdate(obj) {
    // loop object for key name and value and apply to registrationQuery
    if (obj !== null) {
      const newQuery = { ...fromDate, ...obj };
      setFromDate(newQuery);
    }
  }
  function toDateUpdate(obj) {
    // loop object for key name and value and apply to registrationQuery
    if (obj !== null) {
      const newQuery = { ...toDate, ...obj };
      setToDate(newQuery);
    }
  }


  async function handleUrlQuery() {
    // MSAL doesn't allow to use a url with query params, so we need to store it locally when we get it
    // and after redirect read it again, clear it
    const search = router.query;
    updateQuery(search);
  }

  function updateQuery(urlValues) {
    let filterValues = Object.keys(registrationQuery);

    for (let key in filterValues) {
      const element = filterValues[key];
      let startValue = {};

      if (urlValues[element] != null) {
        startValue[element] = urlValues[element];
      }
      if (!registrationQuery[element]) {
        registrationQuery[element] = startValue[element];
      }
      setRegistrationQuery((oldState) => ({ ...oldState, ...startValue }));
    }
  }

  function updateHistory(route = '/') {
    router.push(`${router.pathname}${getQueryString()}`, undefined, { shallow: true });
  }

  function getQueryString() {
    let query = '?';
    for (let key in registrationQuery) {
      const filter = registrationQuery[key];
      if (filter?.length > 0) {
        query += `${key}=${filter}&`;
      }
    }
    return query?.substring(0, query.length - 1);
  }

  return (
    <SearchContext.Provider
      value={{
        globals,
        registrationQuery,
        fromDate,
        toDate,
        registrationQueryUpdate,
        fromDateUpdate,
        toDateUpdate,
        resetRegistrationQuery,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
}

export const useSearch = () => useContext(SearchContext);
